// Лентата-заглавие на блок в решетката (и в картите) на листа
// „Инвентаризация". Решетката е ЕДНА, с три блока — без заглавия те изглеждаха
// като няколко таблици, затова всеки блок казва какво е:
//   • „ЗА БРОЕНЕ · 2 (избрани от списъка)" — закачените от „Целия списък" /
//     „Нов продукт", още НЕпреброени (затова не са под „Преброени");
//   • „ПРЕБРОЕНИ · 12 от 245" с тънка лента за напредъка;
//   • „НЕПРЕБРОЕНИ · 233 — остават както са; напишете 0, ако липсва":
//     непреброеното НЕ се нулира, а липсата се пише изрично.
// Над трите — „ПРИГОТВЕНИ РЕЦЕПТИ · 2 — съставките им се прибавят към
// преброеното" (`kind="recipes"`, секцията на StocktakeRecipeSection). „За
// броене" казва и откъде са редовете: от списъка и/или съставки на рецептите.
// Числата са СЪЩИТЕ като на чиповете за статус (`StocktakeBlock.count` идва от
// `statusCounts`, виж stocktakeView.ts). Лентата не е клетка — стрелките ↑/↓
// я прескачат (нямат `data-rk`).
import { useTranslation } from "react-i18next";
import { ROW_HEADER } from "../../components/list/density";
import type { StocktakeBlock, StocktakeBlockKey } from "./stocktakeView";

// `kind`, не `key` — React не подава `key` като проп
type Props = {
  kind: StocktakeBlockKey | "recipes";
  count: number;
  total: number;
  pinnedFrom?: StocktakeBlock["pinnedFrom"];
};

// Лентата-заглавие на блок — височината на заглавен ред (30px, `ROW_HEADER`)
const BAND_CLASS = `flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-shade-2 bg-fill px-[11px] ${ROW_HEADER} leading-[normal]`;
const LABEL_CLASS = "text-[10.5px] font-bold uppercase tracking-[0.1em]";
const COUNT_CLASS = "font-sheet text-[12px] font-semibold";

// Откъде са закачените: само от списъка (заварената подсказка), само съставки
// на рецепти, или и двете
const pinnedHintKey = (pinnedFrom: Props["pinnedFrom"]) =>
  pinnedFrom?.recipes
    ? pinnedFrom.list
      ? "inventoryPage.sr6BandPinnedBoth"
      : "inventoryPage.sr6BandPinnedRecipes"
    : "inventoryPage.stqBandPinnedHint";

export const StocktakeBlockBand = ({ kind, count, total, pinnedFrom }: Props) => {
  const { t } = useTranslation();
  if (kind === "recipes") {
    return (
      <div className={BAND_CLASS}>
        <span className={`${LABEL_CLASS} text-brand-deep-ink`}>{t("inventoryPage.sr6SectionTitle")}</span>
        <span className={`${COUNT_CLASS} text-ink`}>{count}</span>
        <span className="text-[12px] text-body">— {t("inventoryPage.sr6SectionHint")}</span>
      </div>
    );
  }
  if (kind === "pinned") {
    return (
      <div className={BAND_CLASS}>
        <span className={`${LABEL_CLASS} text-brand-deep-ink`}>{t("inventoryPage.stqStatusPinned")}</span>
        <span className={`${COUNT_CLASS} text-ink`}>{count}</span>
        <span className="text-[12px] text-body">{t(pinnedHintKey(pinnedFrom))}</span>
      </div>
    );
  }
  if (kind === "rest") {
    return (
      <div className={BAND_CLASS}>
        <span className={`${LABEL_CLASS} text-muted`}>{t("inventoryPage.stkStatusUncounted")}</span>
        <span className={`${COUNT_CLASS} text-body`}>{count}</span>
        <span className="text-[12px] text-body">— {t("inventoryPage.stqBandRestHint")}</span>
      </div>
    );
  }
  const percent = total > 0 ? Math.min(100, Math.round((count / total) * 100)) : 0;
  return (
    <div className={BAND_CLASS}>
      <span className={`${LABEL_CLASS} text-ok-deep`}>{t("inventoryPage.stkStatusCounted")}</span>
      <span className={`${COUNT_CLASS} text-ink`}>
        {t("inventoryPage.stkBandProgress", { counted: count, total })}
      </span>
      <span
        role="progressbar"
        aria-label={t("inventoryPage.stkStatusCounted")}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={count}
        className="h-1 w-[120px] overflow-hidden rounded-full bg-shade-2"
      >
        {/* ширината е известна чак по време на изпълнение → inline style */}
        <span className="block h-full rounded-full bg-ok" style={{ width: `${percent}%` }} />
      </span>
    </div>
  );
};

export default StocktakeBlockBand;
